import { Box, Icon, Typography } from "@outschool/backpack";
import { EnrollmentMeetingPurchaseQueryQuery } from "@outschool/gql-frontend-generated";
import { faInfoCircle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { pxToRem } from "@outschool/ui-utils";
import React from "react";

import { ActivityEnrollQueryQuery } from "../../../../shared/generated/graphql";

type SubscriptionDisclaimerProps = {
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
  isWeeklyPayment: boolean;
  oneOnOneNextBillingDate?: Date;
  canPayLater?: boolean;
  isMeetingPrepay?: boolean;
  isSubscriptionCreditPurchase: boolean;
};

// NOTE: 'Subscription' here is different from isSubscriptionCreditPurchase.
export default function SubscriptionDisclaimer({
  activity,
  isWeeklyPayment,
  oneOnOneNextBillingDate,
  canPayLater = false,
  isMeetingPrepay = false,
  isSubscriptionCreditPurchase
}: SubscriptionDisclaimerProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\SubscriptionDisclaimer"
  );

  let disclaimerText;
  let icon = faInfoCircle;
  let headerText;
  if (!!activity.is_ongoing_weekly) {
    disclaimerText = isSubscriptionCreditPurchase
      ? t(
          "Credits are automatically deducted each Sunday. \
      If you have insufficient credits, your saved payment method will be automatically charged for the remaining amount. \
      Visit the classroom to withdraw before Sunday to avoid new charges."
        )
      : t(
          "You will be automatically charged each week on Sunday. To withdraw and stop recurring charges, visit the classroom page."
        );
  } else if (activity.isClub) {
    disclaimerText = isSubscriptionCreditPurchase
      ? t(
          "Credits are automatically deducted at the end of each month. \
      If you have insufficient credits, your saved payment method will be automatically charged for the remaining amount. \
      To cancel, visit the group page."
        )
      : t(
          "You will be automatically charged at the end of each month. To withdraw, visit the group page."
        );
  } else if (isWeeklyPayment) {
    disclaimerText = isSubscriptionCreditPurchase
      ? t(
          "Credits are automatically deducted each Sunday. \
      If you have insufficient credits, your saved payment method will be automatically charged for the remaining amount. \
      Visit the classroom to withdraw before Sunday to avoid new charges."
        )
      : activity.isSelfPaced
      ? t(
          "You will be automatically charged each week on Sunday. To withdraw and stop recurring charges, visit the classroom page."
        )
      : t(
          "You will be automatically charged each week on Sunday until the class ends. To withdraw and stop recurring charges, visit the classroom page."
        );
  } else if (
    activity.hasTeacherSchedule &&
    oneOnOneNextBillingDate &&
    !isMeetingPrepay
  ) {
    headerText = t("Charged Weekly");
    disclaimerText = isSubscriptionCreditPurchase
      ? t(
          "Credits are automatically deducted each Sunday. \
          If you have insufficient credits, your saved payment method will be automatically charged for the remaining amount. \
          Visit the classroom to withdraw before Sunday to avoid new charges."
        )
      : canPayLater
      ? t(
          "Enroll now, pay weekly. You will be automatically charged weekly on Sundays before the class date."
        )
      : t(
          "Enroll now, pay weekly. Today pay the amount identified above and automatically be \
              charged weekly for the recurring meetings on Sundays before the class date."
        );
  }

  if (!disclaimerText) {
    return null;
  }

  return (
    <Box
      sx={{
        fontSize: "0.875em",
        marginTop: "0.75em",
        borderRadius: pxToRem(8),
        padding: pxToRem(16),
        backgroundColor: "white"
      }}
    >
      <Box flex>
        <Icon
          icon={icon}
          sx={{
            fontSize: "1.333em",
            marginTop: "0.25em",
            marginRight: "0.5em"
          }}
        />
        <Box>
          {!!headerText && (
            <Typography variant="subtitle1">{headerText}</Typography>
          )}
          <Typography variant="body2">{disclaimerText}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
